import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { Close } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { BackendUser } from "types/interfaces/adminUsers";
import type { Client } from "types/interfaces/adminUsers";
import {
  ClientAutocompleteListbox,
  clientAutocompletePaperProps,
} from "./clientAutocomplete";
import { useClientsAutocompleteInfinite } from "features/adminUsers/useClientsAutocompleteInfinite";
import {
  useCreateBackendUserMutation,
  useUpdateBackendUserMutation,
  useDeleteBackendUserMutation,
  useLazyCheckBackendUsernameAvailabilityQuery,
  useGetBackendUserFoldersQuery,
  useGetBackendUserIntegrationsQuery,
  useGetMajorJobsQuery,
} from "features/adminUsers/adminUsers";
import { useGetBackendUserSftpConnectionsQuery } from "features/sftp/sftp";
import AppDialog from "../common/AppDialog";
import CustomScrollbars from "../CustomScrollbars";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "features/store";
import ConnectFolderDialog from "components/fileStorage/ConnectFolderDialog";
import { pushNotification } from "features/notificationSlice";
import { startFolderConnectSync } from "features/folderConnectSyncSlice";
import SftpConnectionPanel from "components/sftp/SftpConnectionPanel";
import type { SFTPConnection } from "types/interfaces/sftp";
import {
  getUsernameValidationError,
  pickUsernameServerError,
} from "utils/usernameValidation";

type TabId = "profile" | "folders" | "integrations" | "sftp" | "bots";

interface BackendUserPanelProps {
  user: BackendUser | null;
  onClose: () => void;
  onUserUpdated: (user: BackendUser) => void;
  onUserDeleted?: () => void;
  fillViewport?: boolean;
}

export default function BackendUserPanel({
  user,
  onClose,
  onUserUpdated,
  onUserDeleted,
  fillViewport = false,
}: BackendUserPanelProps) {
  const isCreateMode = user === null;
  const currentUser = useAppSelector((state) => state.auth.user);
  const isSuperUser = Boolean(currentUser?.platform_profile?.is_superuser);
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [tab, setTab] = useState<TabId>("profile");
  const [username, setUsername] = useState(user?.username ?? "");
  const [usernameServerError, setUsernameServerError] = useState<string | null>(
    null,
  );
  const [backendClientName, setBackendClientName] = useState(
    user?.first_name ?? "",
  );
  const [selectedClientId, setSelectedClientId] = useState<number | null>(
    user?.client_id ?? null,
  );
  const [selectedClient, setSelectedClient] = useState<Client | null>(
    user?.client_id != null
      ? ({ id: user.client_id, company_name: user.client_name ?? "" } as Client)
      : null,
  );
  const [clientSearch, setClientSearch] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [createSftpDialogOpen, setCreateSftpDialogOpen] = useState(false);
  const [sftpCreateDirty, setSftpCreateDirty] = useState(false);
  const [backendClientNameTouched, setBackendClientNameTouched] =
    useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.username ?? "");
      setBackendClientName(user.first_name ?? "");
      setSelectedClientId(user.client_id ?? null);
      setSelectedClient(
        user.client_id != null
          ? ({
              id: user.client_id,
              company_name: user.client_name ?? "",
            } as Client)
          : null,
      );
    } else {
      setUsername("");
      setBackendClientName("");
      setSelectedClientId(null);
      setSelectedClient(null);
    }
    setClientSearch("");
    setUsernameServerError(null);
    setBackendClientNameTouched(false);
  }, [
    user?.id,
    user?.username,
    user?.first_name,
    user?.client_id,
    user?.client_name,
  ]);

  const usernameClientError = getUsernameValidationError(username);
  const backendClientNameError =
    backendClientName.trim().length === 0
      ? "Display Name is required."
      : backendClientName.trim().length > 50
        ? "Display name cannot exceed 50 characters."
        : undefined;
  const usernameBlocksSubmit =
    !username.trim() ||
    Boolean(usernameClientError) ||
    Boolean(usernameServerError) ||
    Boolean(backendClientNameError);
  const usernameHelperText =
    usernameClientError ?? usernameServerError ?? undefined;

  const [createUser, { isLoading: isCreating }] =
    useCreateBackendUserMutation();
  const [updateUser, { isLoading: isSaving }] = useUpdateBackendUserMutation();
  const [deleteUser, { isLoading: isDeleting }] =
    useDeleteBackendUserMutation();
  const [checkBackendUsernameAvailability] =
    useLazyCheckBackendUsernameAvailabilityQuery();

  const {
    options: clientOptions,
    loading: clientsLoading,
    onListboxScroll: clientsListboxOnScroll,
  } = useClientsAutocompleteInfinite({
    search: clientSearch,
    value: selectedClient,
  });

  const { data: foldersData, refetch: refetchFoldersData } =
    useGetBackendUserFoldersQuery(user?.id ?? 0, {
      skip: !user || tab !== "folders",
    });

  const { data: integrationsData } = useGetBackendUserIntegrationsQuery(
    user?.id ?? 0,
    {
      skip: !user || tab !== "integrations",
    },
  );

  const { data: sftpData, refetch: refetchSftpConnections } =
    useGetBackendUserSftpConnectionsQuery(user?.id ?? 0, {
      skip: !user || tab !== "sftp",
    });

  const { data: majorJobsData } = useGetMajorJobsQuery(user?.id ?? 0, {
    skip: !user || tab !== "bots",
  });

  const hasProfileChanges =
    !!user &&
    (user.username !== username.trim() ||
      user.first_name !== backendClientName.trim() ||
      user.client_id !== selectedClientId);

  const handleSave = async () => {
    if (!user) return;
    setSaveConfirmOpen(false);
    try {
      const updated = await updateUser({
        id: user.id,
        username: username.trim(),
        first_name: backendClientName.trim(),
        client_id: selectedClientId ?? undefined,
      }).unwrap();
      onUserUpdated(updated);
      showToast("User updated successfully.", "success");
      onClose();
    } catch (err: unknown) {
      const uMsg = pickUsernameServerError(err);
      if (uMsg) setUsernameServerError(uMsg);
      const errors = (err as { data?: { errors?: Record<string, string[]> } })
        ?.data?.errors;
      const msg = errors
        ? Object.values(errors).flat().join(" ")
        : "Failed to save changes.";
      showToast(msg, "error");
    }
  };

  const buildSaveConfirmSections = (): {
    label: string;
    updated: string[];
  }[] => {
    const sections: { label: string; updated: string[] }[] = [];
    const updated: string[] = [];
    if (user && (user.username || "").trim() !== username.trim())
      updated.push("Username");
    if (user && (user.first_name ?? "").trim() !== backendClientName.trim())
      updated.push("Display Name");
    if (user && user.client_id !== selectedClientId) updated.push("Client");
    if (updated.length > 0) sections.push({ label: "PROFILE", updated });
    return sections;
  };

  const handleCreate = async () => {
    if (!backendClientName.trim()) {
      setBackendClientNameTouched(true);
      showToast("Display Name is required", "error");
      return;
    }
    if (!selectedClientId) {
      showToast("Client is required", "error");
      return;
    }
    try {
      const created = await createUser({
        username: username.trim(),
        first_name: backendClientName.trim(),
        client_id: selectedClientId!,
      }).unwrap();
      onUserUpdated(created);
      showToast("User created successfully.", "success");
      onClose();
    } catch (err: unknown) {
      const uMsg = pickUsernameServerError(err);
      if (uMsg) setUsernameServerError(uMsg);
      const errors = (err as { data?: { errors?: Record<string, string[]> } })
        ?.data?.errors;
      const msg = errors
        ? Object.values(errors).flat().join(" ")
        : "Failed to create user.";
      showToast(msg, "error");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!user) return;
    try {
      await deleteUser(user.id).unwrap();
      setDeleteConfirmOpen(false);
      setDeleteConfirmInput("");
      onClose();
      showToast("User deleted. S3 cleanup has been queued.", "success");
      onUserDeleted?.();
    } catch {
      setDeleteConfirmOpen(false);
    }
  };

  const handleDeleteConfirmClose = () => {
    if (!isDeleting) {
      setDeleteConfirmOpen(false);
      setDeleteConfirmInput("");
    }
  };

  const fullName = user
    ? user.client_name || user.username
    : "New Backend User";

  const showToast = (message: string, severity: "success" | "error") => {
    dispatch(pushNotification({ message, severity }));
  };

  useEffect(() => {
    const value = username.trim();
    if (!value || usernameClientError) {
      setUsernameServerError(null);
      return;
    }
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      try {
        const res = await checkBackendUsernameAvailability({
          value,
          excludeId: user?.id,
        }).unwrap();
        if (cancelled) return;
        setUsernameServerError(
          res.available
            ? null
            : (res.message ?? "This username is already in use."),
        );
      } catch {
        if (!cancelled) setUsernameServerError(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [
    username,
    usernameClientError,
    user?.id,
    checkBackendUsernameAvailability,
  ]);

  return (
    <Box
      sx={(muiTheme: Theme) => ({
        width: fillViewport ? "100%" : { xs: "100%", sm: 380, md: 420 },
        maxWidth: "100%",
        minWidth: 0,
        flexShrink: fillViewport ? 1 : 0,
        flex: fillViewport ? 1 : undefined,
        minHeight: fillViewport ? 0 : undefined,
        borderLeft: fillViewport
          ? "none"
          : { xs: "none", sm: `1px solid ${muiTheme.palette.divider}` },
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        overflow: "hidden",
      })}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1,
              bgcolor: "calibrate.primarySoft10",
              display: "grid",
              placeItems: "center",
              fontWeight: 900,
              fontSize: 14,
              color: "primary.main",
            }}
          >
            {fullName[0]?.toUpperCase()}
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={800} lineHeight={1.2}>
              {fullName}
            </Typography>
            {user && (
              <Typography variant="caption" color="text.secondary">
                @{user.username}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          minWidth: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, value: TabId) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ minHeight: 44 }}
          TabIndicatorProps={{ style: { height: 2 } }}
        >
          <Tab
            value="profile"
            label="Profile"
            sx={{
              minHeight: 44,
              fontSize: 13,
              textTransform: "none",
              fontWeight: 700,
              minWidth: "auto",
              px: 1.5,
            }}
          />
          {!isCreateMode && (
            <Tab
              value="folders"
              label="Folders"
              sx={{
                minHeight: 44,
                fontSize: 13,
                textTransform: "none",
                fontWeight: 700,
                minWidth: "auto",
                px: 1.5,
              }}
            />
          )}
          {!isCreateMode && (
            <Tab
              value="integrations"
              label={isNarrow ? "Integr." : "Integrations"}
              sx={{
                minHeight: 44,
                fontSize: 13,
                textTransform: "none",
                minWidth: "auto",
                px: 1.5,
              }}
            />
          )}
          {!isCreateMode && isSuperUser && (
            <Tab
              value="sftp"
              label="SFTP"
              sx={{
                minHeight: 44,
                fontSize: 13,
                textTransform: "none",
                fontWeight: 700,
                minWidth: "auto",
                px: 1.5,
              }}
            />
          )}
          {!isCreateMode && (
            <Tab
              value="bots"
              label="Bots"
              sx={{
                minHeight: 44,
                fontSize: 13,
                textTransform: "none",
                fontWeight: 700,
                minWidth: "auto",
                px: 1.5,
              }}
            />
          )}
        </Tabs>
      </Box>

      {tab === "profile" && (
        <>
          <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
              <TextField
                label="Username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setUsernameServerError(null);
                }}
                size="small"
                fullWidth
                required
                error={Boolean(usernameHelperText)}
                helperText={usernameHelperText}
                sx={{
                  mb: 1.25,
                  "& .MuiOutlinedInput-root": { borderRadius: 1 },
                }}
              />
              <TextField
                label="Display Name"
                value={backendClientName}
                onChange={(e) => setBackendClientName(e.target.value)}
                onBlur={() => setBackendClientNameTouched(true)}
                size="small"
                fullWidth
                required
                error={
                  backendClientNameTouched && Boolean(backendClientNameError)
                }
                helperText={
                  backendClientNameTouched ? backendClientNameError : ""
                }
                sx={{
                  mb: 1.25,
                  "& .MuiOutlinedInput-root": { borderRadius: 1 },
                }}
              />
              <Autocomplete
                options={clientOptions}
                loading={clientsLoading}
                filterOptions={(x) => x}
                getOptionLabel={(option) => option.company_name}
                value={selectedClient}
                onChange={(_, newValue) => {
                  setSelectedClient(newValue);
                  setSelectedClientId(newValue?.id ?? null);
                }}
                onInputChange={(_, v, reason) => {
                  if (reason === "input") setClientSearch(v);
                  else if (reason === "clear") setClientSearch("");
                }}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                size="small"
                fullWidth
                componentsProps={clientAutocompletePaperProps}
                ListboxComponent={ClientAutocompleteListbox}
                ListboxProps={{ onScroll: clientsListboxOnScroll }}
                getOptionDisabled={(c) => Boolean(c.is_archived)}
                renderOption={(props, c) => (
                  <li {...props}>
                    <Box
                      sx={{
                        width: "100%",
                        py: 0.4,
                        opacity: c.is_archived ? 0.45 : 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {c.company_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.7rem", lineHeight: 1.25 }}
                      >
                        {c.is_archived ? "Archived" : c.company_code}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    required
                    sx={{
                      mb: 1.25,
                      "& .MuiOutlinedInput-root": { borderRadius: 1 },
                    }}
                  />
                )}
              />
              {!isCreateMode && user && (
                <Box
                  sx={{
                    mb: 2,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: "grey.50",
                    border: "1px dashed",
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700 }}
                  >
                    S3 User
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                  >
                    {user.id}
                  </Typography>
                </Box>
              )}
            </Box>
          </CustomScrollbars>
          <Divider />
          <DialogActions sx={{ px: 3, py: 1.5, gap: 1, flexWrap: "wrap" }}>
            {isCreateMode ? (
              <Button
                variant="contained"
                onClick={handleCreate}
                disabled={
                  !username.trim() ||
                  !selectedClientId ||
                  isCreating ||
                  usernameBlocksSubmit
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1,
                }}
              >
                {isCreating ? "Creating..." : "Create Backend User"}
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  onClick={() => hasProfileChanges && setSaveConfirmOpen(true)}
                  disabled={
                    !hasProfileChanges || isSaving || usernameBlocksSubmit
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 1,
                  }}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={isDeleting}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 1,
                  }}
                >
                  {isDeleting ? "Deleting..." : "Delete User"}
                </Button>
              </>
            )}
          </DialogActions>
        </>
      )}

      <AppDialog
        open={saveConfirmOpen}
        onClose={() => !isSaving && setSaveConfirmOpen(false)}
        title="Confirm Access Changes"
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() => setSaveConfirmOpen(false)}
              disabled={isSaving}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Confirm
            </Button>
          </>
        }
      >
        {buildSaveConfirmSections().map((section, idx) => (
          <Box key={section.label}>
            {idx > 0 && <Divider sx={{ my: 2 }} />}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing={0.6}
              display="block"
              sx={{ mb: 1 }}
            >
              {section.label}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              letterSpacing={0.5}
              sx={{ color: "info.main", display: "block", mb: 0.5 }}
            >
              Updated
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
              {section.updated.map((item) => (
                <Typography key={item} variant="body2" component="li">
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        ))}
      </AppDialog>

      <AppDialog
        open={deleteConfirmOpen}
        onClose={handleDeleteConfirmClose}
        title="Delete Backend User"
        actions={
          <>
            <Button
              onClick={handleDeleteConfirmClose}
              disabled={isDeleting}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={
                isDeleting ||
                (user?.username ?? "").trim() !== deleteConfirmInput.trim()
              }
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        }
      >
        <Typography
          variant="subtitle2"
          fontWeight={800}
          sx={{ color: "error.main", textTransform: "uppercase", mb: 1.5 }}
        >
          Warning
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Delete {user?.client_name || user?.username} (@{user?.username})? The
          user and their data will be removed. All SFTP connections linked to
          this user will be deleted. S3 cleanup for users/{user?.id} will be
          queued.
        </Typography>
        <TextField
          label="Type username to confirm"
          placeholder={user?.username ?? ""}
          value={deleteConfirmInput}
          onChange={(e) => setDeleteConfirmInput(e.target.value)}
          size="small"
          fullWidth
          autoComplete="off"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
        />
      </AppDialog>

      {tab === "folders" && user && (
        <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              px: 2.5,
              pt: 2,
              pb: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                mb: 0.5,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "grey.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700 }}
              >
                Manage connected folders
              </Typography>
              {isSuperUser && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setConnectDialogOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 1,
                    px: 1.5,
                  }}
                >
                  Connect folder for this user
                </Button>
              )}
            </Box>
            {foldersData && foldersData.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No folders for this user.
              </Typography>
            )}
            {foldersData?.map((folder) => (
              <Box
                key={folder.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.02)", cursor: "pointer" },
                }}
                onClick={() => {
                  if (!folder.folder_prefix) return;
                  navigate(`/data-storage`, {
                    state: {
                      initialPrefix: folder.folder_prefix,
                      initialFname: folder.folder_display || folder.folder,
                      fromUserManagement: true,
                    },
                  });
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {folder.folder_display || folder.folder}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {folder.folder_prefix}
                  </Typography>
                </Box>
                {folder.is_sftp_folder ? (
                  <Chip
                    label="SFTP Source Files"
                    size="small"
                    sx={{
                      bgcolor: "rgba(21,101,192,0.10)",
                      color: "#1565c0",
                      fontWeight: 600,
                    }}
                  />
                ) : folder.is_integration ? (
                  <Chip
                    label="Integration"
                    size="small"
                    sx={{
                      bgcolor: "#fff3e0",
                      color: "#e65100",
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  <Chip
                    label="Migration"
                    size="small"
                    sx={{
                      bgcolor: "#fff8e1",
                      color: "#ff8f00",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </CustomScrollbars>
      )}

      {tab === "integrations" && user && (
        <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              px: 2.5,
              pt: 2,
              pb: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {integrationsData?.matches?.length === 0 &&
              !integrationsData?.platform_integrations?.length && (
                <Typography variant="body2" color="text.secondary">
                  No integrations for this user.
                </Typography>
              )}

            {!!integrationsData?.platform_integrations?.length && (
              <Typography variant="overline" color="text.secondary">
                Integrations (Beta)
              </Typography>
            )}
            {integrationsData?.platform_integrations?.map((integration) => (
              <Box
                key={`beta-${integration.id}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.02)", cursor: "pointer" },
                }}
                onClick={() =>
                  navigate(`/integrations-beta/${integration.id}`, {
                    state: { fromUserManagement: true },
                  })
                }
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {integration.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {integration.slug}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Chip
                    label={integration.is_active ? "Active" : "Disabled"}
                    size="small"
                    sx={{
                      bgcolor: integration.is_active
                        ? "rgba(46,125,50,0.10)"
                        : "rgba(0,0,0,0.06)",
                      color: integration.is_active ? "#2e7d32" : "#616161",
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label="Beta"
                    size="small"
                    sx={{
                      bgcolor: "rgba(21,101,192,0.10)",
                      color: "#1565c0",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>
            ))}

            {!!integrationsData?.matches?.length && (
              <Typography variant="overline" color="text.secondary">
                Legacy integrations
              </Typography>
            )}
            {integrationsData?.matches?.map((match) => (
              <Box
                key={match.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.02)", cursor: "pointer" },
                }}
                onClick={() =>
                  navigate(`/integrations/${match.id}`, {
                    state: { fromUserManagement: true },
                  })
                }
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {match.integration_name}
                  </Typography>
                </Box>
                <Chip
                  label="Integration"
                  size="small"
                  sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600 }}
                />
              </Box>
            ))}
          </Box>
        </CustomScrollbars>
      )}

      {/* SFTP tab */}
      {tab === "sftp" && user && (
        <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              px: 2.5,
              pt: 2,
              pb: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                mb: 0.5,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "grey.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700 }}
              >
                Manage SFTP access
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSftpCreateDirty(false);
                  setCreateSftpDialogOpen(true);
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1,
                  px: 1.5,
                }}
              >
                Create SFTP for this user
              </Button>
            </Box>
            {(!sftpData?.connections || sftpData.connections.length === 0) && (
              <Typography variant="body2" color="text.secondary">
                No SFTP connections for this user.
              </Typography>
            )}
            {sftpData?.connections?.map((conn) => (
              <Box
                key={conn.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.02)", cursor: "pointer" },
                }}
                onClick={() =>
                  navigate(`/sftp-management/${conn.id}`, {
                    state: { fromUserManagement: true },
                  })
                }
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {conn.name || conn.username}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: "monospace" }}
                  >
                    @{conn.username}
                  </Typography>
                </Box>
                <Chip
                  label={conn.is_active ? "Active" : "Inactive"}
                  size="small"
                  sx={
                    conn.is_active
                      ? {
                          bgcolor: "rgba(46,125,50,0.10)",
                          color: "success.dark",
                          fontWeight: 600,
                        }
                      : {
                          bgcolor: "rgba(237,108,2,0.10)",
                          color: "warning.dark",
                          fontWeight: 600,
                        }
                  }
                />
              </Box>
            ))}
          </Box>
        </CustomScrollbars>
      )}

      {/* Bots tab */}
      {tab === "bots" && user && (
        <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              px: 2.5,
              pt: 2,
              pb: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {(!majorJobsData || majorJobsData.length === 0) && (
              <Typography variant="body2" color="text.secondary">
                No bots jobs for this user.
              </Typography>
            )}
            {majorJobsData && majorJobsData.length > 0 && (
              <List disablePadding>
                {majorJobsData.map((mj) => (
                  <ListItemButton
                    key={mj.id}
                    onClick={() => {
                      navigate(`/bots-management/${user.id}/major-job/${mj.id}`);
                      onClose();
                    }}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemText
                      primary={mj.name}
                      secondary={[mj.vendor, mj.bot_kind]
                        .filter(Boolean)
                        .join(" · ")}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 600,
                      }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        </CustomScrollbars>
      )}

      <ConnectFolderDialog
        open={connectDialogOpen}
        onClose={() => setConnectDialogOpen(false)}
        title="Connect folder for this user"
        forcedUser={user}
        onConnected={(created, folderName) => {
          dispatch(
            startFolderConnectSync({
              folderId: created.folder_id,
              folderName: created.folder || folderName,
            }),
          );
          void refetchFoldersData();
        }}
        onError={(message) =>
          dispatch(
            pushNotification({
              message,
              severity: "error",
            }),
          )
        }
      />

      <Dialog
        open={createSftpDialogOpen}
        onClose={(_, reason) => {
          if (sftpCreateDirty) return;
          setCreateSftpDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxWidth: 520 } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {user && (
            <SftpConnectionPanel
              key={user.id}
              connection={null}
              lockedBackendUser={user}
              embeddedInDialog
              onDirtyChange={setSftpCreateDirty}
              onClose={() => {
                setSftpCreateDirty(false);
                setCreateSftpDialogOpen(false);
              }}
              onSaved={(_conn: SFTPConnection) => {
                showToast("SFTP connection created.", "success");
                void refetchSftpConnections();
                setSftpCreateDirty(false);
                setCreateSftpDialogOpen(false);
              }}
              onDeleted={() => {}}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
